import { create } from 'zustand'
import { resolvePlanTier, type PlanTier } from '@/context/plan-constraint-utils'
import { readDevPlanOverride } from '@/context/dev-plan-override'
import { useAccountStore } from './account-store'

/**
 * The resolved plan tier for code that runs outside React (the companion tool
 * executor, the analyst stream, analytics).
 *
 * `PlanConstraintProvider` is the one writer: it publishes the tier it resolved
 * (dev override, then the entitlements payload, then the live subscription) so
 * a gate read through `getResolvedPlanTier()` can never disagree with one read
 * through `usePlanConstraint()`. Null until the provider mounts, and again after
 * it unmounts on sign-out.
 */
interface PlanTierStoreState {
  planTier: PlanTier | null
}

export const usePlanTierStore = create<PlanTierStoreState>(() => ({ planTier: null }))

/**
 * The tier every gate should use. Before the provider has published one, the
 * same resolution runs over the account store, minus the entitlements payload
 * it has no access to.
 */
export function getResolvedPlanTier(): PlanTier {
  const published = usePlanTierStore.getState().planTier
  if (published) return published
  return resolvePlanTier({
    override: readDevPlanOverride(),
    subscriptions: useAccountStore.getState().user?.subscriptions,
  })
}
